import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, SquareCheck, Globe, Image as ImageIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AgentMetaLine } from './ChatMessage';
import { ToolSkeletonCard } from './ToolSkeletonCard';

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT WORKING STATE
//
// What the user looks at while the model is thinking. Replaces three bouncing
// dots that said nothing.
//
// Everything here is TRUE — elapsed time, the model actually being used, and the
// context genuinely sent with this turn. There are no invented progress steps:
// the backend makes a single opaque call, so we cannot know what stage it is at,
// and pretending otherwise would be a lie that eventually shows.
//
// The elapsed-time interval lives HERE rather than in the page, so ticking once
// a second re-renders ~30 nodes instead of the entire chat.
// ═══════════════════════════════════════════════════════════════════════════════

const chipClass =
    'inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] ' +
    'px-2 py-0.5 font-mono text-[10px] font-medium tracking-[0.08em] text-muted-foreground';

function ContextChip({ icon: Icon, label, detail }) {
    return (
        <span className={chipClass}>
            <Icon className="h-3 w-3 opacity-60" />
            <span className="tabular-nums">{label}</span>
            {detail && <span className="max-w-[180px] truncate opacity-50">{detail}</span>}
        </span>
    );
}

export function AgentWorkingState({
    startedAt,
    modelLabel,
    contextFiles = [],
    contextTasks = [],
    fullContext = false,
    webSearch = false,
    imageCount = 0,
    announcedTools = null,
    reduceMotion = false,
}) {
    const [elapsedMs, setElapsedMs] = useState(0);

    useEffect(() => {
        if (!startedAt) return undefined;
        const tick = () => setElapsedMs(Date.now() - startedAt);
        tick();
        const id = setInterval(tick, 100);
        return () => clearInterval(id);
    }, [startedAt]);

    const hasContext =
        fullContext || contextFiles.length > 0 || contextTasks.length > 0 || webSearch || imageCount > 0;

    return (
        <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            // Leaves on a short tween, not the entrance spring. The reply this
            // was standing in for is already rendered by the time the exit
            // starts, so a slow fade shows the same turn twice — once finished,
            // once still "working".
            exit={{ opacity: 0, transition: { duration: reduceMotion ? 0 : 0.12 } }}
            transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 28 }}
            className="w-full"
            data-testid="agent-working"
        >
            <AgentMetaLine modelLabel={modelLabel} elapsedMs={elapsedMs}>
                <span className="ml-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            </AgentMetaLine>

            <div className="min-w-0 border-l-2 border-primary/40 pl-4 md:pl-5">
                {hasContext && (
                    <div className="mb-3 flex flex-wrap items-center gap-1.5">
                        {fullContext ? (
                            <ContextChip icon={FileText} label={`All files`} detail={`${contextFiles.length}`} />
                        ) : contextFiles.length > 0 && (
                            <ContextChip
                                icon={FileText}
                                label={`${contextFiles.length} ${contextFiles.length === 1 ? 'file' : 'files'}`}
                                detail={contextFiles.slice(0, 2).join(', ')}
                            />
                        )}
                        {contextTasks.length > 0 && (
                            <ContextChip
                                icon={SquareCheck}
                                label={`${contextTasks.length} ${contextTasks.length === 1 ? 'task' : 'tasks'}`}
                                detail={contextTasks.slice(0, 2).join(', ')}
                            />
                        )}
                        {webSearch && <ContextChip icon={Globe} label="Web search" />}
                        {imageCount > 0 && (
                            <ContextChip
                                icon={ImageIcon}
                                label={`${imageCount} ${imageCount === 1 ? 'image' : 'images'}`}
                            />
                        )}
                    </div>
                )}

                {/* Prose skeleton — a shape holder, not a progress claim. */}
                {!announcedTools?.length && (
                    <div className="space-y-2.5 py-1" aria-label="Waiting for response">
                        {['w-[92%]', 'w-[78%]', 'w-[45%]'].map((w, i) => (
                            <div key={i} className={cn('forge-skeleton h-3 rounded-full', w)} />
                        ))}
                    </div>
                )}

                {announcedTools?.map((tool, i) => (
                    <ToolSkeletonCard
                        key={`${tool.toolName}-${i}`}
                        toolName={tool.toolName}
                        target={tool.target}
                        status={tool.status || (i === 0 ? 'running' : 'queued')}
                    />
                ))}
            </div>
        </motion.div>
    );
}

export default AgentWorkingState;
